/**
 * FRONTDESK-CHECKIN-AND-IDENTITY-V1 (rider R-08) -- the cancel-reason LABEL
 * module. Pure; tested by direct call (repo idiom: no jsdom).
 *
 * TOKENS are org master data: the vocabulary lives in org_workflow_settings
 * key `appointment.cancel_reason_codes` (THE ONE reader, seeded default =
 * the #247-designed six below) and reaches the client as
 * `me.cancelReasonCodes` (present only for cancel-capable action roles).
 * LABELS live HERE -- display strings never ride the config tokens
 * (the qualification.checklist_items precedent). An org-custom token outside
 * the seeded six gets an honest humanized label, never a hidden option.
 *
 * NO free text: 'other' stands alone (the appointment_events table doctrine
 * forbids free-text notes; the #247 proposal decision 3).
 */

import type { DashboardMe } from '@/dashboard/types';

/** Mirror of DEFAULT_APPOINTMENT_CANCEL_REASON_CODES
 *  (lib/org-workflow-settings.ts) -- the seeded default vocabulary that
 *  serves when /me carries no org list (code defaults serve; zero flags). */
export const DEFAULT_CANCEL_REASON_CODES: readonly string[] = Object.freeze([
  'patient_left_due_to_wait',
  'patient_declined_treatment',
  'doctor_unavailable',
  'wrong_appointment',
  'asked_to_reschedule',
  'other',
]);

/** Fixed labels for the seeded tokens (plain desk words, ASCII only). */
const CANCEL_REASON_LABELS: Readonly<Record<string, string>> = Object.freeze({
  patient_left_due_to_wait: 'Patient left due to wait',
  patient_declined_treatment: 'Patient declined treatment',
  doctor_unavailable: 'Doctor unavailable',
  wrong_appointment: 'Wrong appointment',
  asked_to_reschedule: 'Asked to reschedule',
  other: 'Other',
});

/** Display label for a cancel-reason token: the fixed label for a seeded
 *  token, else an honest humanization (underscores to spaces, sentence
 *  case) for an org-custom token -- never a raw token, never a hidden row. */
export function cancelReasonLabel(code: string): string {
  const fixed = CANCEL_REASON_LABELS[code];
  if (fixed !== undefined) return fixed;
  const spaced = code.replace(/_/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : code;
}

/** The picker's option list: the org's configured vocabulary from /me when
 *  present (cancel-capable roles), else the seeded default. Never empty. */
export function resolveCancelReasonCodes(
  me: Pick<DashboardMe, 'cancelReasonCodes'> | null | undefined,
): readonly string[] {
  const configured = me?.cancelReasonCodes;
  return Array.isArray(configured) && configured.length > 0
    ? configured
    : DEFAULT_CANCEL_REASON_CODES;
}
